import {useActionState, useEffect, useTransition} from 'react';
import usePrevious from './usePrevious';

type ServerFormAction<T> = (formData: FormData) => Promise<T>;

const useServerFormAction = <T>(action: ServerFormAction<T>) => {
  const wrappedAction = async (previousState: T, formData: FormData) => action(formData);

  const [data, formAction] = useActionState(wrappedAction, undefined as Awaited<T>);

  return {data, formAction};
};

export default useServerFormAction;

export type ServerAction<T, U = unknown> = (options: U) => Promise<T>;

type Options<T> = {
  onSuccess?: (data: T) => void;
};

export const useServerAction = <T, U>(serverAction: ServerAction<T, U>, options?: Options<T>) => {
  const wrappedAction = async (previousState: T, options: U) => serverAction(options);

  const [isPending, startTransition] = useTransition();

  const wasPending = usePrevious(isPending);

  const [data, action] = useActionState(wrappedAction, undefined as Awaited<T>);

  useEffect(() => {
    if (wasPending && !isPending && options?.onSuccess) {
      options.onSuccess?.(data);
    }
  }, [wasPending, isPending]);

  return {data, action: (options: U) => startTransition(() => action(options)), isPending};
};
